import { firstValueFrom } from 'rxjs'
import { grpc } from '@improbable-eng/grpc-web'

import { MediaFileBundle, Subclip, Tag } from 'elements-sdk'
import { Logger } from 'common/log'
import { OperatingSystem } from 'common/util/services/operatingSystem.service'
import { Time } from 'media/time'
import type { MCAPITypes, MCAPIClient } from 'vendor'
// eslint-disable-next-line no-duplicate-imports
import { loadMCAPI } from 'vendor'

// eslint-disable-next-line no-restricted-imports
import { GetListOfBinItemsRequestBody_BinItemFlags } from '../../../static/vendor/MCAPI_Types'
import { BaseInterface, loadAssetContentsPages } from './base'

interface MediaComposerWindow extends Window {
  mcapi?: any
  mc?: any
}
declare let window: MediaComposerWindow

const TAGS_COLUMN = 'ELEMENTS Tags'

export class MediaComposerInterface extends BaseInterface {
  override logger = new Logger('MediaComposerInterface')

  proxyMode = false
  metadata: Record<string, any>
  mcapiclient: MCAPIClient
  currentFolder: string | null = null
  override native = true
  override hasProxy = false

  async init (): Promise<void> {
    this.app = this.getApp()
    this.active = true

    window.mc = this

    const { MCAPIClient, GrpcWeb } = await loadMCAPI()
    this.platform = this.getPlatform()
    this.metadata = {
      accessToken: window.mcapi.getAccessToken(),
    }

    this.mcapiclient = new MCAPIClient(
      new GrpcWeb(window.mcapi.getGatewayServerAddress(), {
        metadata: new grpc.Metadata(this.metadata),
      })
    )

    return Promise.resolve()
  }

  getFS () {
    return null
  }

  getApp () {
    return 'MediaComposer'
  }

  getPlatform () {
    return PLATFORM_MAP[this.operatingSystem.get('Windows')]
  }

  hasRestart () {
    return true
  }

  restart () {
    this.logger.warn('restart()')
    window.location.href = 'avpi://src/index.html'
  }

  override async importAsset (asset, _subclips?: Subclip[]) {
    const response = await this.locateItem({ asset: asset.id })
    return this.importFile(response.path)
  }

  async getActiveSequencePaths () {
    this.logger.warn('getActiveSequencePaths()')
    return []
  }

  async getProjectPaths () {
    this.logger.warn('getProjectPaths()')
    return []
  }

  attachProxies (paths) {
    this.logger.warn('importAssets()', paths)
  }

  async isProxyMode () {
    return this.proxyMode
  }

  setProxyMode (enabled) {
    this.proxyMode = enabled
  }

  async open (item) {
    try {
      if (item.asset?.type === 'fcpxml') {
        await this.importEditorProject(item.mainfile.id)
      } else {
        const subclips = await this.querySubclipImport([item])
        await this.importAsset(item.asset, subclips)
      }
    } catch (error) {
      this.notify.error('Error while opening item', error)
    }
  }

  getConfig (key: string): string | null {
    return window.localStorage.getItem(key)
  }

  setConfig (key: string, value: string) {
    window.localStorage.setItem(key, value)
  }

  hasConfig () {
    return true
  }

  getLocalPath () {
    throw new Error('Method not implemented.')
  }

  currentBinPath (name: string) {
    if (this.currentFolder) {
      return `${this.currentFolder}/${name}.avb`
    } else {
      return `${name}.avb`
    }
  }

  private async ensureTagsColumn (absoluteBinPath: string) {
    const columnInfo: MCAPITypes.GetBinColumnInfoResponse = await this.mcapiclient.GetBinColumnInfo({
      body: { binPath: absoluteBinPath },
    })
    const tagsColumn = columnInfo.body?.column.find(col => col.columnName === TAGS_COLUMN)
    if (!tagsColumn) {
      await this.mcapiclient.CreateCustomColumn({
        body: { absoluteBinPath, newColumnName: TAGS_COLUMN },
      })
    }
  }

  override async importPath (path: string, type = 'import') {
    const binPath = this.currentBinPath('ELEMENTS')
    try {
      const bininfo = await this.getBin(binPath)
      if (!bininfo?.isOpen) {
        await this.openBin(binPath)
      }
    } catch {
      await this.createBin('ELEMENTS', this.currentFolder ?? undefined)
    }

    let result: MCAPITypes.ImportFileResponse | MCAPITypes.LinkFileResponse
    if (type === 'import') {
      this.logger.info('Import File: ', path)
      result = await this.mcapiclient.ImportFile({
        body: {
          filePath: path,
          destinationBin: binPath,
        },
      })
    } else {
      this.logger.info('Link File: ', path)
      result = await this.mcapiclient.LinkFile({
        body: {
          filePath: path,
          destinationBin: binPath,
        },
      })
    }
    return result.body?.mobId
  }

  /**
   * Finds first matching file inside a bin. Polls GetListOfBinItems every 500ms for up to 10 seconds until the file is found.
   *
   * Although Avid's ImportFile method claims to return the `mobId` of the successfully imported item,
   * in practice only LinkFile does this. Therefore, this method searches the bin for the last imported
   * file with the specified name instead.
   *
   * @param binPath - relative path of the bin to search
   * @param name - name of the file (mob) to find
   *
   * @returns `mobId` of the found file or `undefined` if not found
   */
  async findFile (binPath: string, name: string, timeoutMs = 10_000): Promise<string | undefined> {
    const deadline = Date.now() + timeoutMs

    while (Date.now() < deadline) {
      const { body } = await firstValueFrom(
        this.mcapiclient.GetListOfBinItems({
          body: {
            binRelativePath: binPath,
            binFlags: [GetListOfBinItemsRequestBody_BinItemFlags.AllTypes],
          },
        })
      )

      if (body?.mobName === name) return body.mobId
      await new Promise(r => setTimeout(r, 500))
    }

    return undefined
  }

  async getBin (path: string): Promise<MCAPITypes.GetBinInfoResponseBody | null> {
    const result = await this.mcapiclient.GetBinInfo({
      body: {
        relativeBinPath: path,
      },
    })
    return result.body ?? null
  }

  async openBin (path: string): Promise<MCAPITypes.OpenBinResponseBody | null> {
    const result = await this.mcapiclient.OpenBin({
      body: {
        binPath: path,
        locked: true,
      },
    })
    return result.body ?? null
  }

  async createBin (name: string, path: string | undefined): Promise<MCAPITypes.CreateBinResponseBody | null> {
    const result = await this.mcapiclient.CreateBin({
      body: {
        binName: name,
        folderPath: path,
      },
    })
    return result.body ?? null
  }

  async ensureBin (binPath: string) {
    try {
      const binInfo = await this.getBin(binPath)
      if (!binInfo?.isOpen) {
        await this.openBin(binPath)
      }
    } catch {
      const splitPath = binPath.split('/')
      const folder = splitPath.slice(0, -1).join('/')
      await this.createBin('ELEMENTS', folder)
    }
  }

  async importDirs (dirs, folder: string | null, type = 'import') {
    folder = folder ?? this.currentFolder
    try {
      for (const dir of dirs) {
        // Unfortunely there is no way to create a folder currently
        // const subfolder = folder ? `${folder}/${dir.name}` : dir.name
        // const binPath = `${subfolder}/ELEMENTS.avb`
        const subfolder = null
        const binPath = 'ELEMENTS.avb'
        await this.ensureBin(binPath)

        await loadAssetContentsPages(this.api, dir, async (bundles, directories) => {
          if (bundles.length) {
            this.currentFolder = subfolder
            await this.importBundles(bundles, type)
          }
          if (directories.length) {
            await this.importDirs(directories, subfolder, type)
          }
        })
      }
    } finally {
      this.currentFolder = folder
    }
  }

  override async importBundles (bundles: MediaFileBundle[], type = 'import') {
    if (!bundles.length) {
      return
    }

    const response = await this.locateItem({ path: bundles[0]!.mainfile.fullPath })
    await this.mountWorkspaces([response.workspace])

    for (const bundle of bundles) {
      if (bundle.asset) {
        if (bundle.asset.type === 'fcpxml') {
          await this.importEditorProject(bundle.mainfile.id)
        } else if (['video', 'audio', 'image'].includes(bundle.asset.type!)) {
          try {
            const bundleFiles = await this.api.mediaLibrary.getAllMediaFilesForBundles({
              allMediaFilesForBundlesRequest: {
                bundles: [bundle.id],
              },
            })
            const mobId = await this.importBundleFiles(bundleFiles.map(file => file.fullPath), type, bundle.name)
            if (mobId) {
              await this.importTags(mobId, bundle.asset.tags)
            }
          } catch (error) {
            this.notify.error('Error while opening item', error)
          }
        }
      }
    }
  }

  async importTags (mobId: string, tags: Tag[]) {
    const binPath = this.currentBinPath('ELEMENTS')
    if (tags.length) {
      await this.ensureTagsColumn(binPath)
      await this.mcapiclient.SetMobInfo({
        body: {
          mobId,
          column: { columnName: TAGS_COLUMN, columnValue: tags.map(t => `[${t.name}]`).join(',') },
        },
      })
    }
  }

  async importBundleFiles (paths: string[], type = 'import', bundleName: string) {
    const urls: string[] = []
    const binPath = this.currentBinPath('ELEMENTS')

    this.logger.info('Importing bundle files:', paths)

    const mediafileRegex = /([/\\])Avid MediaFiles\1/
    let avidMediaFileMatch: RegExpMatchArray | null = null

    for (const path of paths) {
      const url = await this.clientSideUrl.getPathUrl(path, this.platform)
      urls.push(url)

      const match = url.match(mediafileRegex)
      avidMediaFileMatch = match
    }

    if (avidMediaFileMatch) {
      const response = await this.api.private.checkPMR({
        checkPMREndpointRequest: {
          path: paths[0]!,
        },
      })
      if (!response.indexed) {
        this.notify.warning('The Bundle is not indexed in the msmdb.')
      }

      await this.importAvidMediaFile(urls, avidMediaFileMatch)
      return this.findFile(binPath, bundleName)
    } else {
      for (const path of urls) {
        await this.importPath(path, type)
      }
      return await this.findFile(binPath, bundleName.substring(0, bundleName.lastIndexOf('.'))) ?? this.findFile(binPath, bundleName)
    }
  }

  async importAvidMediaFile (urls: string[], match: RegExpMatchArray) {
    const sep: string = match[1]!
    this.logger.info('Import AvidMediaFile:', urls)

    const path = urls[0]!

    const files = urls.map(url => url.split(sep).slice(-1).join())
    const splitPath = path.split(sep)
    const folder = splitPath.slice(0, -1).join(sep)

    await this.mcapiclient.ScanAvidMediaFilesFolder({
      body: {
        destinationPath: folder,
      },
    })

    await this.ensureBin('ELEMENTS.avb')
    const clipsresult = await this.mcapiclient.CreateClipsFromAvidMediaFilesFolder({
      body: {
        binPath: 'ELEMENTS.avb',
        mediaFolderPath: folder,
        mediaFilePath: files,
      },
    })
    if (clipsresult.header?.warnings) {
      this.logger.warn(clipsresult.header.warnings)
    }
  }

  override async importEditorProject (fileId: number) {
    this.logger.info('Importing project:', fileId)

    try {
      const response = await this.api.mediaLibrary.locateEditorProjectPaths({ id: fileId })
      await this.mountWorkspaces(new Set(response.map(x => x.workspace).filter(x => x)))
    } catch (error) {
      this.logger.warn('Failed to mount workspaces:', error)
    }

    const path = await this.elementsClient.loadAAF(fileId)
    await this.importPath(path)
  }

  getSystemPath (): string {
    throw new Error('Method not implemented.')
  }

  async getActiveSequenceID () {
    return null
  }

  async getActiveSequenceTimestamp () {
    return Time.zero
  }

  async setActiveSequenceTimestamp () {
    throw new Error('Method not implemented.')
  }
}

const PLATFORM_MAP: Readonly<Record<OperatingSystem, 'osx' | 'win'>> = {
  'macOS': 'osx',
  'Windows': 'win',
  'Linux': 'win',
}
